import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, FormControl,
    InputLabel, Select, MenuItem, useMediaQuery, useTheme
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import api from '../../../services/api';

const UserFormDialog = ({ open, onClose, currentUser, fetchUsers, showSnackbar }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const initialFormData = {
        name: '',
        email: '',
        type: '',
        password: ''
    };

    const [formData, setFormData] = useState(initialFormData);
    const [isFormChanged, setIsFormChanged] = useState(false);

    useEffect(() => {
        if (currentUser) {
            setFormData({
                name: currentUser.name || '',
                email: currentUser.email || '',
                type: currentUser.type || '',
                password: ''
            });
            setIsFormChanged(false);
        } else {
            setFormData(initialFormData);
            setIsFormChanged(false);
        }
    }, [currentUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newFormData = { ...prev, [name]: value };
            if (currentUser) {
                const isChanged =
                    newFormData.name !== (currentUser.name || '') ||
                    newFormData.email !== (currentUser.email || '') ||
                    newFormData.type !== (currentUser.type || '') ||
                    newFormData.password !== '';
                setIsFormChanged(isChanged);
            }
            return newFormData;
        });
    };

    const handleSubmit = async () => {
        try {
            if (!formData.name || !formData.email || !formData.type) {
                showSnackbar('Preencha todos os campos obrigatórios', 'error');
                return;
            }
            const payload = {
                name: formData.name,
                email: formData.email,
                type: formData.type,
                ...(formData.password && { password: formData.password })
            };
            if (currentUser) {
                await api.put(`/users/${currentUser.id}`, payload);
                showSnackbar('Usuário atualizado com sucesso.', 'success');
            } else {
                await api.post('/users', payload);
                showSnackbar('Usuário cadastrado com sucesso.', 'success');
            }
            fetchUsers();
            setFormData(initialFormData);
            setIsFormChanged(false);
            onClose();
        } catch (error) {
            const message = error.response?.data?.error || 'Erro ao processar a solicitação';
            showSnackbar(message, 'error');
            console.error('Error processing user:', error);
        }
    };

    const handleClose = () => {
        setFormData(initialFormData);
        setIsFormChanged(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth={isMobile ? false : isTablet ? 'sm' : 'md'}
            fullScreen={isMobile}
            sx={{
                '& .MuiDialog-paper': {
                    width: isMobile ? '100%' : isTablet ? '80%' : '35%',
                    margin: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
                    color: 'white',
                    textAlign: 'center',
                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                    py: isMobile ? 1.5 : 2,
                }}
            >
                {currentUser ? 'Editar Usuário' : 'Novo Usuário'}
            </DialogTitle>

            <DialogContent
                sx={{
                    mt: isMobile ? 2 : 3,
                    px: isMobile ? 2 : isTablet ? 3 : 4,
                    py: isMobile ? 1 : 2,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Grid container direction="column" spacing={isMobile ? 1 : 1} sx={{ width: '100%', maxWidth: isMobile ? '100%' : '600px' }}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            fullWidth
                            label="Nome (Obrigatório)"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            margin="normal"
                            variant="outlined"
                            sx={{
                                maxWidth: isMobile ? '100%' : isTablet ? '400px' : '450px',
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#10641c',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#10641c',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            fullWidth
                            label="Email (Obrigatório)"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            margin="normal"
                            variant="outlined"
                            sx={{
                                maxWidth: isMobile ? '100%' : isTablet ? '400px' : '450px',
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#10641c',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#10641c',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <FormControl
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            sx={{
                                maxWidth: isMobile ? '100%' : isTablet ? '400px' : '450px',
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#10641c',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#10641c',
                                },
                                '& .MuiSelect-select': {
                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                },
                            }}
                        >
                            <InputLabel id="user-type-label">Tipo (Obrigatório)</InputLabel>
                            <Select
                                labelId="user-type-label"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                label="Tipo (Obrigatório)"
                            >
                                <MenuItem value="admin">Administrador</MenuItem>
                                <MenuItem value="team">Equipe Técnica</MenuItem>
                                <MenuItem value="city_hall">Prefeitura</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {currentUser && (
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <TextField
                                fullWidth
                                label="Senha (Opcional)"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleChange}
                                margin="normal"
                                variant="outlined"
                                sx={{
                                    maxWidth: isMobile ? '100%' : isTablet ? '400px' : '450px',
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#10641c',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#10641c',
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: isMobile ? '0.9rem' : '1rem',
                                    },
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: isMobile ? 2 : isTablet ? 2.5 : 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: isMobile ? 1 : 2,
                    flexDirection: isMobile ? 'column' : 'row',
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{
                        bgcolor: '#ff0000',
                        color: 'white',
                        '&:hover': { bgcolor: '#cc0000' },
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        px: isMobile ? 2 : 3,
                        width: isMobile ? '100%' : '150px',
                        borderRadius: 1,
                        textTransform: 'uppercase',
                    }}
                    startIcon={<CloseIcon />}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={currentUser ? !isFormChanged : !formData.name || !formData.email || !formData.type}
                    sx={{
                        bgcolor: '#10641c',
                        '&:hover': { bgcolor: '#0a3d0f' },
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        px: isMobile ? 2 : 3,
                        width: isMobile ? '100%' : '150px',
                        borderRadius: 1,
                        marginRight: '6px',
                        textTransform: 'uppercase',
                    }}
                    startIcon={<SaveIcon />}
                >
                    {currentUser ? 'Atualizar' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserFormDialog;